import { defineComponent as _defineComponent } from 'vue'
import { computed, ComputedRef, onBeforeUnmount, Ref, ref, watch } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import ModalDialog from '@/components/dialogs/ModalDialog.vue';
import PrimaryButton from '@/components/form/PrimaryButton.vue';
import { useRouter } from '@/composables/router/VueRouter';
import { AppName } from '@/data/Branding';
import BrowserDetection from '@/data/BrowserDetection';
import { tokenExpiryMillis } from '@/data/helpers/TokenHelper';
import IosNative from '@/data/IosNative';
import RouteNames from '@/router/RouteNames';
import { useConnectionStore } from '@/stores/Connection';
import { useUserStore } from '@/stores/User';


export default /*#__PURE__*/_defineComponent({
  __name: 'ConnectionErrorDialog',
  setup(__props) {

const userStore = useUserStore();
const connectionStore = useConnectionStore();

const router = useRouter();

/**
 * If we have auth failure for a duration greater than SHOW_FAILURE_FAILSAFE_SECS
 * then we show the auth pop-up whatever the silent refresh status.
 */
const SHOW_FAILURE_FAILSAFE_SECS: number = 60; // 1 min

const refreshEPOCTIntervalId: Ref<ReturnType<typeof setInterval> | null> = ref(null);

/**
 * Variable used to track changing EPOC and ensure the authTokenExpired getter will
 * update when the failsafe reaches its timeout.
 */
const approxEPOCInSecs: Ref<number> = ref(Math.floor(Date.now() / 1000));

const authFailingSinceEPOC: Ref<number | null> = ref(null);

const authFailureDetected: ComputedRef<boolean> = computed(() => {
  return connectionStore.connectionState?.authFailure ?? false;
});

const authTokenExpired: ComputedRef<boolean> = computed(() => {
  // Some auth servers don't renew tokens, but re-issue them with the same end date, causing a short period
  // where auth fails but a refresh will work.
  // Prevent an avoidable short-lived error dialog by waiting for a silent refresh failure first.
  const confirmedAuthFailure = authFailureDetected.value && connectionStore.silentRefreshFailing;

  // If we have a persistent auth error despite silent refresh succeeding, the app won't work. Catch
  // this scenario and allow the error to display.
  const authFailureNotResolving = authFailingSinceEPOC.value === null
    ? false
    : (authFailingSinceEPOC.value + SHOW_FAILURE_FAILSAFE_SECS) < approxEPOCInSecs.value;

  // If we wake from sleep then we can fall into a scenario where we don't show an error for a while due to
  // events having been missed during sleep. Here we will show an error message if we know that the token has
  // expired and we can't renew it. Note that `approxEPOCInSecs` will make this evaluate within 1s of waking.
  const tokenExpiredAndUnableToRenew = connectionStore.silentRefreshFailing && (tokenExpiryMillis() < Date.now());

  return confirmedAuthFailure || authFailureNotResolving || tokenExpiredAndUnableToRenew;
});

const nonAuthConnectionFailure: ComputedRef<boolean> = computed(() => {
  // Currently the WorkerServerData.ts code treats auth failures as also being "connection down"
  // (See in particular the flagServerConnectionDown method).
  // But here we need to report specifically about connection issues that are not auth issues...
  return (connectionStore.connectionState?.connectionDown ?? false) && !authFailureDetected.value;
});

const shouldShow: ComputedRef<boolean> = computed(() => {
  if (userStore.isGuestUser && authTokenExpired.value) {
    // If we are a guest user we don't need to worry about auth token issues
    return false;
  }

  return authTokenExpired.value || nonAuthConnectionFailure.value;
});

const reconnectPageRoute: ComputedRef<RouteLocationRaw> = computed(() => {
  return { name: RouteNames.RECONNECT_ROUTE_NAME };
});

function refreshPage(): void {
  if (BrowserDetection.isIosApp()) {
    const currentPage: string = `${window.location}`;
    IosNative.invokeNative('reconnect', currentPage);
  } else {
    const reconnectPath = router.resolve(reconnectPageRoute.value);

    let args = '?forceReauth=true';
    if (userStore.currentTokenARID) {
      // If there is an existing login context (i.e. a specific SAML login for example)
      // then ensure the reconnect window will enforce its use...
      args = args + '&arid=' + encodeURIComponent(userStore.currentTokenARID);
    }

    window.open(reconnectPath.href + args, '_blank');
  }
}

function onkey(event: KeyboardEvent): void {
  // Don't handle events unless the component is active.
  if (!shouldShow.value) {
    return;
  }
  if ((event.key === 'Enter')) {
    refreshPage();
  }
}

watch(() => authFailureDetected.value, (newValue: boolean, oldValue?: boolean) => {
  if (newValue && !oldValue) {
    authFailingSinceEPOC.value = Math.floor(Date.now() / 1000);
  } else if (!newValue) {
    authFailingSinceEPOC.value = null;
  }

  // iOS has its own token refresh mechanism, so only trigger it for complete auth failures
  // (not silent refresh issues).
  if (BrowserDetection.isIosApp() && newValue) {
    IosNative.invokeNative('log', `${AppName} auth token expired, informing native app...`);
    if (!IosNative.invokeNative('authTokenExpired')) {
      IosNative.invokeNative('log', 'current app version does not support auth refresh, so must login');
      IosNative.invokeNative('startNativeLogin', { debugInfo: 'ConnectionErrorDialog::authTokenExpiredChanged' });
    }
  }
}, { immediate: true });

onBeforeUnmount(() => {
  if (refreshEPOCTIntervalId.value) {
    clearInterval(refreshEPOCTIntervalId.value);
  }
  window.removeEventListener('keydown', onkey);
});

window.addEventListener('keydown', onkey);
refreshEPOCTIntervalId.value = setInterval(() => {
  approxEPOCInSecs.value = Math.floor(Date.now() / 1000);
}, 1000); // Refresh every second

return { __sfc: true,userStore, connectionStore, router, SHOW_FAILURE_FAILSAFE_SECS, refreshEPOCTIntervalId, approxEPOCInSecs, authFailingSinceEPOC, authFailureDetected, authTokenExpired, nonAuthConnectionFailure, shouldShow, reconnectPageRoute, refreshPage, onkey, ModalDialog, PrimaryButton }
}

})